import * as React from 'react';
import * as ReactDOM from 'react-dom';
import s from './client.scss';
import Actions from './actions';
import i18n from '../../../i18n/client-i18n';
import { I18nextProvider } from 'react-i18next';
import { PriceQuoteView, PriceQuoteDialog } from '@wix/price-quote-view';
import { PriceQuoteActions } from './PriceQuoteActions/PriceQuoteActions';
import { ErrorBoundary } from '../../../api/error-reporter/error-reporter';

import { getLocale } from './get-locale';
import { ConcurrentWarning } from './ConcurrentWarning';
import { GeneralErrorNotification } from './GeneralErrorNotification/GeneralErrorNotification';
import { HttpClient } from '@wix/http-client';
import '@wix/design-system/styles.global.css';
import { Experiments } from '../../../constants/experiments';

const baseURL = window.location.origin + '/';
const httpClient = new HttpClient({ baseURL });
const actions = new Actions(
  window.__TOKEN__,
  window.__INSTANCE_ID__,
  window.__PRICE_QUOTE_ID__,
  httpClient,
);

const { experiments, invoiceTemplate } = window.__DATA__;

const CONCURRENT_ERROR = 'concurrent_error';
const PRICE_QUOTE_BASE_URL = `https://quotes.wix.com`;

interface PriceQuoteViewClientProps {
  priceQuote: any;
  businessInfo: any;
}

interface PriceQuoteViewClientState {
  isDialogOpen: boolean;
  inProcess: boolean;
  isCloseConcurrentWarningOpened: boolean;
  priceQuote: any;
  businessInfo: any;
  showGeneralErrorToast: boolean;
}

class PriceQuoteViewClient extends React.Component<
  PriceQuoteViewClientProps,
  PriceQuoteViewClientState
> {
  state = {
    isDialogOpen: false,
    inProcess: false,
    isCloseConcurrentWarningOpened: false,
    priceQuote: this.props.priceQuote,
    businessInfo: this.props.businessInfo,
    showGeneralErrorToast: false,
  };

  openDialog() {
    this.setState({ isDialogOpen: true });
  }

  closeDialog() {
    this.setState({ isDialogOpen: false });
  }

  closeConcurrentWarning = () => {
    this.setState({ isCloseConcurrentWarningOpened: false });
  };

  showConcurrentWarning = () => {
    this.setState({ isCloseConcurrentWarningOpened: true });
  };

  refreshData = () => {
    location.reload();
  };

  showToaster() {
    this.setState({ inProcess: false });
    this.setState({ showGeneralErrorToast: true });
  }
  hideToaster() {
    this.setState({ showGeneralErrorToast: false });
  }

  render() {
    const acceptAndRefresh = async () => {
      if (!this.state.inProcess) {
        this.setState({ inProcess: true });
        try {
          try {
            await actions.accept();
          } catch (e) {
            if (e.response?.status === 409) {
              this.showConcurrentWarning();
              throw new Error(CONCURRENT_ERROR);
            } else {
              this.showToaster();
            }
          }

          const { priceQuote, businessInfo } = await actions.get();
          this.setState({ priceQuote, businessInfo }, () => this.openDialog());
        } catch (error) {
          if (error.message && error.message.includes(CONCURRENT_ERROR)) {
            console.error('Failure due to concurrency error');
          } else {
            console.error('Failed to load accepted price quote dialog');
            this.showToaster();
          }
        }
      }
    };

    const enableTaxedLineTotals =
      experiments.experiments[Experiments.PriceQuoteTaxedLineTotals] === 'true';
    return (
      <div
        data-hook="price-quote-view-client"
        className={s.container}
        style={
          window.__INITIAL_I18N__.locale === 'ja'
            ? ({ '--wsr-font-family': 'Noto Sans CJK JP Regular' } as any)
            : {}
        }
      >
        <GeneralErrorNotification
          showGeneralError={this.state.showGeneralErrorToast}
          onClose={() => this.hideToaster()}
        />
        <ConcurrentWarning
          closeModal={this.closeConcurrentWarning}
          isModalOpened={this.state.isCloseConcurrentWarningOpened}
          onRefreshData={this.refreshData}
        />
        <div className={s.actionsHeader}>
          <PriceQuoteActions
            status={this.state.priceQuote.status}
            askAQuestion={() =>
              window.chat?.expand() ?? window.Chat.expandWidget()
            }
            withAskAQuestion={!!window.Chat}
            onAccept={acceptAndRefresh}
            print={() => window.print()}
            pdfUrl={actions.pdfUrl({
              urlPrefix: window.__CONSENT_POLICY_ENABLED__
                ? window.__URL_PREFIX__
                : PRICE_QUOTE_BASE_URL,
            })}
            token={window.__TOKEN__}
          />
        </div>
        <div className={s.content}>
          <PriceQuoteView
            priceQuote={this.state.priceQuote}
            businessInfo={this.state.businessInfo}
            locale={getLocale(this.state.priceQuote)}
            isUoU
            invoiceTemplate={invoiceTemplate}
            features={{
              enableTaxedLineTotals,
            }}
          />
        </div>
        <PriceQuoteDialog
          onClose={() => this.closeDialog()}
          isOpen={this.state.isDialogOpen}
          businessName={this.state.businessInfo.name}
          withInvoice={!!this.state.priceQuote.invoiceId}
        />
      </div>
    );
  }
}

export const app = () => (
  <ErrorBoundary>
    <I18nextProvider i18n={i18n(window.__INITIAL_I18N__)}>
      <PriceQuoteViewClient
        priceQuote={window.__PRICE_QUOTE__}
        businessInfo={window.__BUSINESS_INFO__}
      />
    </I18nextProvider>
  </ErrorBoundary>
);

ReactDOM.render(app(), document.getElementById('root'));
